import { useId } from "react";
import { useTranslation } from "react-i18next";

import BaseDropdown from "@/components_v2/FormFields/Dropdown/BaseDropdown";
import InfiniteScrollDropdown from "@/components_v2/FormFields/Dropdown/InfiniteScrollDropdown";
import { useLazyGetUsersQuery } from "@/redux/service";
import {
  DEFAULT_FILTERS,
  STATUS_FILTERS,
} from "@/features/community/moderation/constants";
import {
  ModerationFiltersProps,
  StatusFilter,
} from "@/features/community/moderation/types";

import "../styles.css";

const ModerationFilters = ({ filters, onChange }: ModerationFiltersProps) => {
  const { t } = useTranslation();
  const statusId = useId();
  const authorId = useId();
  const [getUsers] = useLazyGetUsersQuery();
  const isDefault =
    filters.status === DEFAULT_FILTERS.status &&
    filters.author === DEFAULT_FILTERS.author;

  return (
    <aside className="moderation-filters flex flex-col gap-400">
      <div className="flex items-center justify-between gap-200">
        <h2 className="moderation-filters-title body-m-medium">
          {t("moderation.filters.title")}
        </h2>

        <button
          type="button"
          className="moderation-link-button body-s-medium"
          disabled={isDefault}
          onClick={() =>
            onChange({
              ...filters,
              status: DEFAULT_FILTERS.status,
              author: DEFAULT_FILTERS.author,
            })
          }
        >
          {t("moderation.filters.reset")}
        </button>
      </div>

      <div className="flex flex-col gap-100">
        <label htmlFor={statusId} className="body-s-medium">
          {t("moderation.filters.status")}
        </label>

        <BaseDropdown
          id={statusId}
          value={filters.status}
          options={STATUS_FILTERS.map((status) => ({
            value: status,
            label: t(`moderation.filters.statuses.${status}`),
          }))}
          onChange={(status: StatusFilter) => onChange({ ...filters, status })}
        />
      </div>

      <div className="flex flex-col gap-100">
        <label htmlFor={authorId} className="body-s-medium">
          {t("moderation.filters.author")}
        </label>

        <InfiniteScrollDropdown
          id={authorId}
          value={filters.author ?? undefined}
          fetchFn={getUsers}
          labelFieldName="fullName"
          valueFieldName="id"
          lookupFieldName="ids"
          searchFieldName="fullName"
          placeholder={t("moderation.filters.authorPlaceholder")}
          allowClear
          onChange={(author?: number) =>
            onChange({ ...filters, author: author ?? null })
          }
        />
      </div>
    </aside>
  );
};

export default ModerationFilters;
